/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'
import reactivityImg from 'assets/images/reactivity.jpg'
import data_flowImg from 'assets/images/data-flow.jpg'
import state_hoistingImg from 'assets/images/state-hoisting.gif'
import deep_state_hoistingImg from 'assets/images/deep-state-hoisting.gif'

export const _3 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>3 - Внутреннее состояние компонента</h2>
    <p className={styles.description}>
      Объект-состояния <code className={styles.code}>state</code> это свойство класса которое не должно изменяться
      разработчиком напрямую.{' '}
    </p>
    <ul className={styles.list}>
      <li className={styles.list__item}>
        Данные в <code className={styles.code}>state</code> контролируют то, что отображается в интерфейсе.
      </li>
      <li className={styles.list__item}>
        Данные, хранящиеся в состоянии, должны быть информацией, которая будет обновляться методами компонента.
      </li>
      <li className={styles.list__item}>
        Не нужно дублировать данные из <code className={styles.code}>props</code> в состоянии.
      </li>
      <li className={styles.list__item}>
        Каждый раз, когда изменяется состояние компонента (или пропсы), вызывается метод{' '}
        <code className={styles.code}>render()</code>.
      </li>
    </ul>
    <p className={styles.description}>
      В состоянии хранят минимально необходимый набор данных, на основе которых можно вычислить все необходимое для
      отрисовки интерфейса.{' '}
    </p>
    <p className={styles.description}>
      Это делается вызовом селекторов (функций которые составляют данные для интерфейса на основе состояния) в методе{' '}
      <code className={styles.code}>render()</code>. Так мы получаем вычисляемые данные.{' '}
    </p>
    <img className={styles.img} src={reactivityImg} alt="reactivity" />
    <ul className={styles.list}>
      <li className={styles.list__item}>Интерфейс зависит от состояния компонента.</li>
      <li className={styles.list__item}>Состояние может измениться как реакция на действия пользователя.</li>
      <li className={styles.list__item}>При изменении состояния, данные передаются вниз по дереву компонентов.</li>
      <li className={styles.list__item}>Компоненты возвращают обновленную разметку и изменяется интерфейс.</li>
    </ul>
    <p className={styles.description}>
      Состояние принадлежит компоненту и изменяется только его методами. Изменение состояния компонента никогда не
      повлияет на его родителя, соседей или любой другой компонент в приложении - только на его дочерние элементы. При
      такой модели, данные в приложении передаются только одним, жестко ограниченным образом. Это называется
      однонаправленный поток данных.{' '}
    </p>
    <img className={styles.img} src={data_flowImg} alt="data-flow" />
    <p className={styles.description}>
      Состояние объявляется в конструкторе, так как это первое, что происходит, когда создается экземпляр класса.{' '}
    </p>
    <CopyBlock
      text={`class Counter extends Component {
  constructor() {
    super();

    this.state = {
      value: 0,
    };
  }

  /* ... */

  render() {
    return (
      <div>
        <span>{this.state.value}</span>
        {/* ... */}
      </div>
    );
  }
}
`}
      language="javascript"
      showLineNumbers={false}
      theme={ocean}
    />
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.1 - Начальное состояние от props</h3>
      <p className={styles.description}>
        Иногда начальное состояние зависит от переданных пропсов, например начальное значение нашего счетчика. В этом
        случае, необходимо явно объявить параметр <code className={styles.code}>props</code> в конструкторе и передать
        его в вызов <code className={styles.code}>super(props)</code>. Тогда в конструкторе будет доступно{' '}
        <code className={styles.code}>this.props</code>.{' '}
      </p>
      <CopyBlock
        text={`class Counter extends Component {
  static defaultProps = {
    step: 1,
    initialValue: 0,
  };

  constructor(props) {
    super(props);

    this.state = {
      value: this.props.initialValue,
    };
  }

  /* ... */
}

ReactDOM.render(<Counter initialValue={10} />, document.getElementById('root'));
`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Так как под капотом используется Babel, можно пропустить утомительное объявление конструктора и указать
        состояние как публичное свойство класса, все остальное транспайлер сделает за нас.{' '}
      </p>
      <CopyBlock
        text={`class Counter extends Component {
  static defaultProps = {
    step: 1,
    initialValue: 0,
  };

  state = {
    value: this.props.initialValue,
  };

  /* ... */
}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.2 - Изменение состояния компонента</h3>
      <p className={styles.description}>
        Для обновления состояния используется встроенный метод <code className={styles.code}>setState()</code>
      </p>
      <CopyBlock text={`setState(updater, callback)`} language="javascript" showLineNumbers={false} theme={ocean} />
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Первым, обязательным аргументом, передается объект с полями указывающими какую часть состояния необходимо
          изменить.
        </li>
        <li className={styles.list__item}>
          Вторым, необязательным аргументом, можно передать callback-функцию которая выполнится после изменения
          состояния.
        </li>
      </ul>
      <p className={styles.notificationWarn}>
        Нельзя изменять состояние напрямую по ссылке. Будьте очень внимательны, особенно при работе со ссылочными типами
        (массив, объект).{' '}
      </p>
      <CopyBlock
        text={`state = { fullName: 'Poly' };

// ❌ Плохо
this.state.fullName = 'Mango';

// ✅ Хорошо
this.setState({
  fullName: 'Mango',
});`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Этот подход используется когда новое состояние не рассчитывается на основе предыдущего. То есть когда в
        состояние записывается что-то новое, перезаписывая уже существующее. Сделаем компонент с переключателем, методы
        которого будут перезаписывать значение <code className={styles.code}>isOpen</code> в состоянии.{' '}
      </p>
      <CopyBlock
        text={`class Toggle extends Component {
  state = { isOpen: false };

  show = () => this.setState({ isOpen: true });

  hide = () => this.setState({ isOpen: false });

  render() {
    const { isOpen } = this.state;
    const { children } = this.props;

    return (
      <>
        <button onClick={this.show}>Show</button>
        <button onClick={this.hide}>Hide</button>
        {isOpen && children}
      </>
    );
  }
}
`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.3 - Как обновляется состояние</h3>
      <p className={styles.description}>
        При вызове <code className={styles.code}>setState()</code> не нужно передавать все свойства хранящиеся в
        состоянии. Достаточно указать только ту часть (срез) состояния, которую мы хотим изменить в данной операции.
        React затем берет текущее состояние и объект, который был передан в{' '}
        <code className={styles.code}>setState()</code>, объединяя их следующим образом.{' '}
      </p>
      <CopyBlock
        text={`// состояние перед объединением
const currentState = { a: 2, b: 3, c: 7, d: 9 };

// объект переданный в setState
const updateSlice = { b: 5, d: 4 };

// новое значение this.state после объединения
const nextState = { ...currentState, ...updateSlice }; // {a: 2, b: 5, c: 7, d: 4}
`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.4 - Асинхронность обновления состояния</h3>
      <p className={styles.description}>
        Метод <code className={styles.code}>setState()</code> регистрирует асинхронную операцию обновления состояния,
        которая ставится в очередь обновлений. React изменяет состояние не для каждого вызова{' '}
        <code className={styles.code}>setState()</code>, а может объединять несколько вызовов в одно обновление для
        повышения производительности. Из-за этого доступ к <code className={styles.code}>this.state</code>, в синхронном
        коде, после вызова этого метода вернет значение до обновления.{' '}
      </p>
      <p className={styles.description}>
        Представьте, что при изменении состояния, вы полагаетесь на текущее значение состояния при вычислении
        следующего. Используем цикл <code className={styles.code}>for</code> для создания (регистрации) нескольких
        обновлений.{' '}
      </p>
      <CopyBlock
        text={`// Предположим что есть такое состояние
state = { value: 0 };

// Запустим цикл и создадим 3 операции обновления
for (let i = 0; i < 3; i += 1) {
  /*
   * Если посмотреть состояние, на всех итерациях будет 0
   * Потому что это синхронный код и обновление состояния еще не произошло
   */
  console.log(this.state.value);

  this.setState({ value: this.state.value + 1 });
}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Значение свойства <code className={styles.code}>this.state.value</code> запоминается во время создания объекта
        передаваемого в <code className={styles.code}>setState()</code>, а не во время обновления состояния. То есть,
        если в момент создания объекта, this.state.value содержало <code className={styles.code}>0</code>, в функцию{' '}
        <code className={styles.code}>setState()</code> передается объект{' '}
        <code className={styles.code}>{`{value: 0 + 1}`}</code>.{' '}
      </p>
      <p className={styles.description}>
        В результате выполнения цикла получаем очередь из 3-х объектов{' '}
        <code className={styles.code}>{`{ value: 0 + 1 }, { value: 0 + 1 }, { value: 0 + 1 }`}</code> и оригинальное
        состояние на момент обновления <code className={styles.code}>{`{ value: 0 }`}</code>. После всех обновлений
        получаем состояние <code className={styles.code}>{`{ value: 1 }`}</code>.{' '}
      </p>
      <p className={styles.description}>
        Поэтому нельзя полагаться на текущее состояние при вычислении следующего, зависящего от предыдущего на момент
        обновления. Это может привести к ошибкам. Поэтому существует второй способ обновить состояние.{' '}
      </p>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.5 - setState с функцией</h3>
      <p className={styles.description}>
        Этот подход используется, когда новое значение вычисляется на основе предыдущего состояния. Метод{' '}
        <code className={styles.code}>setState()</code>, первым аргументом, может принимать не объект, а функцию,
        которая должна возвращать объект которым мы хотим обновить состояние.{' '}
      </p>
      <CopyBlock
        text={`setState((state, props) => {
  return {}
}, callback)
`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Актуальное состояние и пропы, на момент асинхронного исполнения функции переданной в{' '}
        <code className={styles.code}>setState()</code>, будут переданы в нее аргументами{' '}
        <code className={styles.code}>state</code> и <code className={styles.code}>props</code>. Таким образом, можно
        быть уверенными в корректном значении предыдущего состояния при создании следующего.{' '}
      </p>
      <CopyBlock
        text={`// Предположим что есть такое состояние
state = { value: 0 };

// Запустим цикл и создадим 3 операции обновления
for (let i = 0; i < 3; i += 1) {
  /*
   * Если посмотреть состояние, на всех итерациях будет 0
   * Потому что это синхронный код и обновление состояния еще не произошло
   */
  console.log(this.state.value); // 0

  this.setState(prevState => {
    /*
     * Если посмотреть состояние переданное callback-функции во время ее вызова,
     * получим актуальное состояния на момент обновления.
     */
    console.log(prevState.value); // будет разный на каждой итерации

    return { value: prevState.value + 1 };
  });
}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Каждый раз, во время вызова функции переданной в <code className={styles.code}>setState()</code>, в параметр{' '}
        <code className={styles.code}>prevState</code> будет передана ссылка на актуальное состояние в момент
        обновления. Получим объекты обновлений{' '}
        <code className={styles.code}>{`{value: 0 + 1}, {value: 1 + 1}, {value: 2 + 1},`}</code> и, в результате,{' '}
        <code className={styles.code}>this.state.value</code> будет содержать <code className={styles.code}>3</code>.{' '}
      </p>
      <p className={styles.description}>
        Теперь можем заменить функционал открыть/закрыть в компоненте <code className={styles.code}>{`<Toggle>`}</code>.{' '}
      </p>
      <CopyBlock
        text={`class Toggle extends Component {
  state = { isOpen: false };

  toggle = () => {
    this.setState(state => ({ isOpen: !state.isOpen }));
  };

  render() {
    const { isOpen } = this.state;
    const { children } = this.props;

    return (
      <div>
        <button onClick={this.toggle}>{isOpen ? 'Hide' : 'Show'}</button>
        {isOpen && children}
      </div>
    );
  }
}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>А счетчик будет выглядеть так. </p>
      <CopyBlock
        text={`class Counter extends Component {
  /* ... */

  handleIncrement = () => {
    this.setState((state, props) => ({
      value: state.value + props.step,
    }));
  };

  handleDecrement = () => {
    this.setState((state, props) => ({
      value: state.value - props.step,
    }));
  };

  /* ... */
}`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>3.6 - Подъем состояния (state hoisting)</h3>
      <p className={styles.description}>
        Так как React использует однонаправленный поток данных сверху вниз, для того, чтобы изменить состояние родителя
        при событии в ребенке, используется следующий паттерн с callback-функцией.{' '}
      </p>
      <img className={styles.img} src={state_hoistingImg} alt="state-hoisting" />
      <ul className={styles.list}>
        <li className={styles.list__item}>В родителе есть состояние и метод который его изменяет.</li>
        <li className={styles.list__item}>
          Ребенку, в виде пропа, пробрасывается метод родителя изменяющий состояние родителя.
        </li>
        <li className={styles.list__item}>В ребенке происходит вызов переданного ему метода.</li>
        <li className={styles.list__item}>При вызове этого метода изменяется состояние родителя.</li>
        <li className={styles.list__item}>Происходит ре-рендер поддерева компонентов родителя.</li>
      </ul>
      <p className={styles.description}>Рассмотрим простой, но наглядный пример. </p>
      <CopyBlock
        text={`/*
 * Button получает функцию changeMessage (имя пропа),
 * которая вызывается при событии onClick
 */
const Button = ({ changeMessage, label }) => (
  <button type="button" onClick={changeMessage}>
    {label}
  </button>
);

class App extends Component {
  state = {
    message: new Date().toLocaleTimeString(),
  };

  // Метод который будем передавать в Button для вызова при клике
  updateMessage = evt => {
    console.log(evt); // Доступен объект события

    this.setState({
      message: new Date().toLocaleTimeString(),
    });
  };

  render() {
    return (
      <>
        <span>{this.state.message}</span>
        <Button label="Change message" changeMessage={this.updateMessage} />
      </>
    );
  }
}
`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        При клике кнопки, состояние <code className={styles.code}>App</code> обновляется с помощью callback-функции,
        контекст которой привязан к <code className={styles.code}>App</code>. Этот паттерн устанавливает четкую границу
        между "умными" и "глупыми" компонентами.{' '}
      </p>
      <p className={styles.description}>Паттерн подъема состояния может иметь любую вложенность. </p>
      <img className={styles.img} src={deep_state_hoistingImg} alt="deep-state-hoisting" />
    </section>
  </section>
))
